import React, { useState, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import SEOHead from "../../components/SEO/SEOHead";
import { doc, getDoc, collection, query, where, getDocs } from "firebase/firestore";
import { db } from "../../firebaseFunctions/config";
import {
  getPublicTicketTypes,
  createTicketCheckoutSession,
  claimFreeTicket,
  joinWaitlist
} from "../../services/TicketingService";
import { Ticket, MapPin, Calendar, CheckCircle, XCircle, Loader, Tag, Users, ExternalLink } from "lucide-react";
import SocialMediaHandles from "../../components/SocialMediaHandles/SocialMediaHandles";

const EventPage = () => {
  const { eventId } = useParams();
  const [searchParams] = useSearchParams();
  const canceled = searchParams.get("canceled") === "1";

  const [event, setEvent] = useState(null);
  const [ticketTypes, setTicketTypes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [attendeeCount, setAttendeeCount] = useState(null);
  const [vendors, setVendors] = useState([]);

  // Purchase form
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [buyerName, setBuyerName] = useState("");
  const [buyerEmail, setBuyerEmail] = useState("");
  const [promoCode, setPromoCode] = useState("");
  const [promoApplied, setPromoApplied] = useState(false);
  const [showPromo, setShowPromo] = useState(false);
  const [purchasing, setPurchasing] = useState(false);
  const [purchaseError, setPurchaseError] = useState(null);

  // Waitlist
  const [showWaitlist, setShowWaitlist] = useState(false);
  const [waitlistName, setWaitlistName] = useState("");
  const [waitlistEmail, setWaitlistEmail] = useState("");
  const [waitlistTicketId, setWaitlistTicketId] = useState(null);
  const [waitlistSubmitted, setWaitlistSubmitted] = useState(false);
  const [waitlistSubmitting, setWaitlistSubmitting] = useState(false);

  useEffect(() => {
    if (eventId) loadEvent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  const loadEvent = async () => {
    setIsLoading(true);
    try {
      const [eventSnap, ticketsResult, vendorsSnap] = await Promise.all([
        getDoc(doc(db, "events", eventId)),
        getPublicTicketTypes(eventId),
        getDocs(query(
          collection(db, "mpsEventRegistration"),
          where("eventId", "==", eventId),
          where("applicationStatus", "in", ["Registered", "Approved"])
        )),
      ]);

      if (!eventSnap.exists()) { setError("Event not found."); return; }

      setEvent({ id: eventSnap.id, ...eventSnap.data() });
      if (ticketsResult.success) setTicketTypes(ticketsResult.ticketTypes);

      // Attendee count (social proof) — sum soldCount across ticket types
      if (ticketsResult.success) {
        const total = ticketsResult.ticketTypes.reduce((sum, t) => sum + (t.soldCount || 0), 0);
        if (total > 0) setAttendeeCount(total);
      }

      // Approved/registered vendors for showcase
      const vendorList = vendorsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      setVendors(vendorList);
    } catch (err) {
      setError("Failed to load event.");
    } finally {
      setIsLoading(false);
    }
  };

  const handlePurchase = async () => {
    if (!selectedTicket) return;
    if (!buyerName.trim()) { setPurchaseError("Please enter your name."); return; }
    if (!buyerEmail.trim() || !/\S+@\S+\.\S+/.test(buyerEmail)) {
      setPurchaseError("Please enter a valid email address.");
      return;
    }

    setPurchasing(true);
    setPurchaseError(null);

    // Free ticket fast path
    if (selectedTicket.price === 0) {
      const result = await claimFreeTicket({
        eventId, ticketTypeId: selectedTicket.id,
        buyerName: buyerName.trim(), buyerEmail: buyerEmail.trim(),
      });
      if (result.success) {
        window.location.href = `/events/${eventId}/success?purchase_id=${result.purchaseId}`;
      } else {
        setPurchaseError(result.error || "Failed to claim ticket.");
        setPurchasing(false);
      }
      return;
    }

    // Paid ticket → Stripe
    const result = await createTicketCheckoutSession({
      eventId, ticketTypeId: selectedTicket.id,
      buyerName: buyerName.trim(), buyerEmail: buyerEmail.trim(),
      promoCode: promoApplied ? promoCode.trim() : undefined,
    });

    if (result.success && result.url) {
      window.location.href = result.url;
    } else {
      setPurchaseError(result.error || "Failed to start checkout. Please try again.");
      setPurchasing(false);
    }
  };

  const handleJoinWaitlist = async () => {
    if (!waitlistName.trim() || !/\S+@\S+\.\S+/.test(waitlistEmail)) {
      return;
    }
    setWaitlistSubmitting(true);
    await joinWaitlist({ eventId, ticketTypeId: waitlistTicketId, name: waitlistName, email: waitlistEmail });
    setWaitlistSubmitted(true);
    setWaitlistSubmitting(false);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr + "T00:00:00");
    return d.toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" });
  };

  if (isLoading) return (
    <div className="min-h-screen flex items-center justify-center bg-grey-50">
      <Loader className="w-8 h-8 text-grey-400 animate-spin" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen flex items-center justify-center bg-grey-50">
      <div className="text-center">
        <XCircle className="w-12 h-12 text-red-400 mx-auto mb-3" />
        <p className="text-grey-700 font-medium">{error}</p>
      </div>
    </div>
  );

  const hasAnyTickets = ticketTypes.length > 0;

  // Build structured data for Google Event schema
  const eventStructuredData = event ? {
    '@context': 'https://schema.org',
    '@type': 'Event',
    name: event.eventName,
    description: event.eventDescription || '',
    image: event.eventCoverPhoto || 'https://app.usepebble.app/og-share-image.png',
    url: `https://app.usepebble.app/events/${eventId}`,
    startDate: event.startDate?.toDate ? event.startDate.toDate().toISOString() : event.startDate,
    endDate: event.endDate?.toDate ? event.endDate.toDate().toISOString() : event.endDate,
    ...(event.location ? {
      location: {
        '@type': 'Place',
        name: event.location,
        address: { '@type': 'PostalAddress', addressLocality: event.location }
      }
    } : {}),
    organizer: {
      '@type': 'Organization',
      name: event.organizerName || 'Pebble Event',
      url: 'https://app.usepebble.app'
    },
    ...(hasAnyTickets ? {
      offers: ticketTypes.map(t => ({
        '@type': 'Offer',
        name: t.name,
        price: t.price ?? 0,
        priceCurrency: 'USD',
        availability: t.soldOut
          ? 'https://schema.org/SoldOut'
          : 'https://schema.org/InStock',
        url: `https://app.usepebble.app/events/${eventId}`
      }))
    } : {})
  } : null;

  const seoDescription = event
    ? `${event.eventDescription?.slice(0, 120) || event.eventName}${event.location ? ` · ${event.location}` : ''}${attendeeCount ? ` · ${attendeeCount} attending` : ''}`
    : undefined;

  return (
    <div className="min-h-screen bg-grey-50">
      {event && (
        <SEOHead
          title={event.eventName}
          description={seoDescription}
          image={event.eventCoverPhoto || undefined}
          url={`https://app.usepebble.app/events/${eventId}`}
          type="event"
          structuredData={eventStructuredData}
        />
      )}
      {canceled && (
        <div className="bg-yellow-50 border-b border-yellow-200 px-4 py-3 text-center text-sm text-yellow-800">
          Checkout was canceled — you were not charged.
        </div>
      )}

      {/* Event header */}
      <div className="bg-white border-b border-grey-200">
        <div className="max-w-2xl mx-auto px-4 py-8">
          {event?.eventCoverPhoto && (
            <img src={event.eventCoverPhoto} alt={event.eventName}
              className="w-full h-52 object-cover rounded-2xl mb-6" />
          )}

          <h1 className="text-2xl font-semibold text-grey-900 mb-4">{event?.eventName}</h1>

          <div className="flex flex-col gap-2 mb-4">
            {(event?.startDate) && (
              <div className="flex items-center gap-2 text-sm text-grey-600">
                <Calendar className="w-4 h-4 flex-shrink-0 text-grey-400" />
                <span>{formatDate(event.startDate)}{event.startTime ? ` · ${event.startTime}` : ""}</span>
              </div>
            )}
            {event?.location && (
              <div className="flex items-center gap-2 text-sm text-grey-600">
                <MapPin className="w-4 h-4 flex-shrink-0 text-grey-400" />
                <span>{event.location}</span>
              </div>
            )}
            {attendeeCount > 0 && (
              <div className="flex items-center gap-2 text-sm text-grey-600">
                <Users className="w-4 h-4 flex-shrink-0 text-grey-400" />
                <span><strong>{attendeeCount}</strong> {attendeeCount === 1 ? "person going" : "people going"}</span>
              </div>
            )}
          </div>

          {event?.eventDescription && (
            <p className="text-sm text-grey-600 leading-relaxed">{event.eventDescription}</p>
          )}

          {/* EO social links */}
          {event?.socialMediaHandles && Object.values(event.socialMediaHandles).some(v => v) && (
            <div className="mt-4 pt-4 border-t border-grey-100 flex items-center gap-2">
              <span className="text-xs text-grey-400 mr-1">Follow</span>
              <SocialMediaHandles handles={event.socialMediaHandles} />
            </div>
          )}

          {event?.eoEventWebsiteURL && (
            <a
              href={event.eoEventWebsiteURL.startsWith('http') ? event.eoEventWebsiteURL : `https://${event.eoEventWebsiteURL}`}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-3 inline-flex items-center gap-1.5 text-sm text-primary-600 hover:text-primary-700 font-medium"
            >
              <ExternalLink className="w-3.5 h-3.5" />
              Visit website
            </a>
          )}
        </div>
      </div>

      {/* Tickets section */}
      <div className="max-w-2xl mx-auto px-4 py-8">
        <h2 className="text-lg font-semibold text-grey-900 mb-4">Tickets</h2>

        {!hasAnyTickets ? (
          <div className="bg-white border border-grey-200 rounded-2xl p-8 text-center">
            <Ticket className="w-10 h-10 text-grey-300 mx-auto mb-3" />
            <p className="text-grey-500">No tickets available at this time.</p>
          </div>
        ) : (
          <>
            {/* Ticket type cards */}
            <div className="space-y-3 mb-6">
              {ticketTypes.map(tt => {
                const remaining = tt.totalQuantity - (tt.soldCount || 0);
                const soldOut = remaining <= 0;
                const isSelected = selectedTicket?.id === tt.id;

                return (
                  <div key={tt.id}>
                    <button
                      onClick={() => {
                        if (soldOut) {
                          setWaitlistTicketId(tt.id);
                          setShowWaitlist(true);
                          return;
                        }
                        setSelectedTicket(isSelected ? null : tt);
                        setPurchaseError(null);
                      }}
                      className={`w-full text-left p-4 rounded-2xl border-2 transition-all ${
                        soldOut
                          ? "border-grey-200 bg-grey-50 cursor-default opacity-60"
                          : isSelected
                          ? "border-grey-900 bg-grey-900"
                          : "border-grey-200 bg-white hover:border-grey-400 hover:shadow-sm"
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <p className={`font-medium ${isSelected && !soldOut ? "text-white" : "text-grey-900"}`}>
                            {tt.name}
                          </p>
                          {tt.description && (
                            <p className={`text-sm mt-0.5 ${isSelected && !soldOut ? "text-grey-300" : "text-grey-500"}`}>
                              {tt.description}
                            </p>
                          )}
                          <div className="flex items-center gap-3 mt-1">
                            {soldOut ? (
                              <span className="text-xs text-red-500 font-medium">Sold out</span>
                            ) : (
                              <span className={`text-xs ${isSelected ? "text-grey-400" : "text-grey-400"}`}>
                                {remaining <= 10 ? `Only ${remaining} left` : `${remaining} available`}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="ml-4 flex-shrink-0 text-right">
                          <p className={`text-lg font-semibold ${isSelected && !soldOut ? "text-white" : "text-grey-900"}`}>
                            {tt.price === 0 ? "Free" : `$${tt.price.toFixed(2)}`}
                          </p>
                          {soldOut && (
                            <p className="text-xs text-grey-400 mt-0.5">Join waitlist</p>
                          )}
                        </div>
                      </div>
                    </button>
                  </div>
                );
              })}
            </div>

            {/* Purchase form */}
            {selectedTicket && (
              <div className="bg-white border border-grey-200 rounded-2xl p-6">
                <h3 className="font-semibold text-grey-900 mb-5">Your Details</h3>

                {purchaseError && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
                    {purchaseError}
                  </div>
                )}

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-grey-700 mb-1.5">Full Name</label>
                    <input
                      type="text"
                      value={buyerName}
                      onChange={e => setBuyerName(e.target.value)}
                      placeholder="Jane Smith"
                      className="w-full px-3.5 py-2.5 border border-grey-300 rounded-xl focus:ring-2 focus:ring-grey-900 focus:border-transparent text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-grey-700 mb-1.5">Email Address</label>
                    <input
                      type="email"
                      value={buyerEmail}
                      onChange={e => setBuyerEmail(e.target.value)}
                      placeholder="jane@example.com"
                      className="w-full px-3.5 py-2.5 border border-grey-300 rounded-xl focus:ring-2 focus:ring-grey-900 focus:border-transparent text-sm"
                    />
                    <p className="text-xs text-grey-400 mt-1">Your ticket and confirmation will be sent here.</p>
                  </div>

                  {/* Promo code */}
                  {selectedTicket.price > 0 && (
                    <div>
                      {!showPromo ? (
                        <button
                          onClick={() => setShowPromo(true)}
                          className="flex items-center gap-1.5 text-sm text-grey-500 hover:text-grey-700 transition"
                        >
                          <Tag className="w-3.5 h-3.5" />
                          Have a promo code?
                        </button>
                      ) : (
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={promoCode}
                            onChange={e => { setPromoCode(e.target.value.toUpperCase()); setPromoApplied(false); }}
                            placeholder="PROMO CODE"
                            className="flex-1 px-3.5 py-2.5 border border-grey-300 rounded-xl focus:ring-2 focus:ring-grey-900 focus:border-transparent text-sm font-mono tracking-wider"
                          />
                          <button
                            onClick={() => promoCode.trim() && setPromoApplied(true)}
                            className="px-4 py-2.5 bg-grey-100 text-grey-700 text-sm rounded-xl hover:bg-grey-200 transition font-medium"
                          >
                            Apply
                          </button>
                        </div>
                      )}
                      {promoApplied && (
                        <div className="flex items-center gap-1.5 mt-2 text-sm text-green-600">
                          <CheckCircle className="w-3.5 h-3.5" />
                          Promo code applied — discount reflected at checkout
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Order summary + CTA */}
                <div className="mt-5 pt-4 border-t border-grey-100">
                  <div className="flex items-center justify-between text-sm mb-4">
                    <span className="text-grey-600">{selectedTicket.name}</span>
                    <span className="font-semibold text-grey-900">
                      {selectedTicket.price === 0 ? "Free" : `$${selectedTicket.price.toFixed(2)}`}
                    </span>
                  </div>
                  <button
                    onClick={handlePurchase}
                    disabled={purchasing}
                    className="w-full py-3.5 bg-grey-900 text-white rounded-xl font-medium hover:bg-grey-800 transition-all disabled:opacity-50 flex items-center justify-center gap-2 text-sm"
                  >
                    {purchasing ? (
                      <><Loader className="w-4 h-4 animate-spin" /> Processing...</>
                    ) : selectedTicket.price === 0 ? (
                      <><Ticket className="w-4 h-4" /> Get Free Ticket</>
                    ) : (
                      <><Ticket className="w-4 h-4" /> Continue to Payment</>
                    )}
                  </button>
                  <p className="text-xs text-grey-400 text-center mt-2">
                    {selectedTicket.price === 0
                      ? "No payment required. Confirmation sent to your email."
                      : "Secure checkout powered by Stripe."}
                  </p>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Vendor showcase */}
      {vendors.length > 0 && (
        <div className="max-w-2xl mx-auto px-4 pb-10">
          <h2 className="text-lg font-semibold text-grey-900 mb-4">
            Vendors ({vendors.length})
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {vendors.map(v => (
              <div key={v.id} className="bg-white border border-grey-200 rounded-2xl p-4 flex flex-col items-center text-center gap-2">
                {v.companyLogo ? (
                  <img src={v.companyLogo} alt={v.businessName} className="w-12 h-12 rounded-full object-cover" />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-grey-100 flex items-center justify-center">
                    <span className="text-lg font-bold text-grey-400">
                      {(v.businessName || "?").slice(0, 1).toUpperCase()}
                    </span>
                  </div>
                )}
                <div>
                  <p className="text-sm font-semibold text-grey-900 line-clamp-1">{v.businessName || "Vendor"}</p>
                  {(v.typeOfBusiness || v.businessType) && (
                    <p className="text-xs text-grey-500 mt-0.5 line-clamp-1">{v.typeOfBusiness || v.businessType}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Waitlist modal */}
      {showWaitlist && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-sm w-full p-6">
            {waitlistSubmitted ? (
              <div className="text-center">
                <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
                <h3 className="font-semibold text-grey-900 mb-1">You're on the list!</h3>
                <p className="text-sm text-grey-500 mb-4">We'll email you if a spot opens up.</p>
                <button onClick={() => { setShowWaitlist(false); setWaitlistSubmitted(false); }}
                  className="w-full py-2.5 bg-grey-900 text-white rounded-xl text-sm font-medium">Done</button>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-grey-900">Join Waitlist</h3>
                  <button onClick={() => setShowWaitlist(false)}
                    className="p-1 hover:bg-grey-100 rounded-lg transition">
                    <XCircle className="w-5 h-5 text-grey-400" />
                  </button>
                </div>
                <p className="text-sm text-grey-500 mb-4">
                  This ticket is sold out. Leave your info and we'll notify you if a spot opens up.
                </p>
                <div className="space-y-3">
                  <input type="text" value={waitlistName} onChange={e => setWaitlistName(e.target.value)}
                    placeholder="Your name"
                    className="w-full px-3.5 py-2.5 border border-grey-300 rounded-xl text-sm focus:ring-2 focus:ring-grey-900 focus:border-transparent" />
                  <input type="email" value={waitlistEmail} onChange={e => setWaitlistEmail(e.target.value)}
                    placeholder="your@email.com"
                    className="w-full px-3.5 py-2.5 border border-grey-300 rounded-xl text-sm focus:ring-2 focus:ring-grey-900 focus:border-transparent" />
                  <button onClick={handleJoinWaitlist} disabled={waitlistSubmitting}
                    className="w-full py-2.5 bg-grey-900 text-white rounded-xl text-sm font-medium disabled:opacity-50">
                    {waitlistSubmitting ? "Joining..." : "Notify Me"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default EventPage;
